import { useUser } from '@folklore/auth';
import { useWindowSize } from '@folklore/hooks';
import { useUrlGenerator, useRoutesMatch } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import { lazy, startTransition, Suspense, useEffect, useState } from 'react';

import useBrandFromLocation from '../../hooks/useBrandFromLocation';
import { useBumperAd } from '../../hooks/useBumperAd';
import useCurrentUrl from '../../hooks/useCurrentUrl';
import useElementSize from '../../hooks/useElementSize';
import useScrollRestoration from '../../hooks/useScrollRestoration';
import useStableWindowHeight from '../../hooks/useStableWindowHeight';
import { useMutateTimeline } from '../../hooks/useTimeline';
import useTimelineCurrentPage from '../../hooks/useTimelineCurrentPage';
import useTimelineNavigation, { useTimelineHistory } from '../../hooks/useTimelineNavigation';
import useTimelineVisibleRange from '../../hooks/useTimelineVisibleRange';
import { useWindowScrolledPast, useWindowScrollValue } from '../../hooks/useWindowScrollValue';
import { getPathname } from '../../lib/timeline';
import useScrollTracking from '../../lib/useScrollTracking';
import { mergeRefs } from '../../lib/utils';

import useSidebarSpring from '../../animations/useSidebarSpring';
import useTimelineSpring from '../../animations/useTimelineSpring';
import { useConsent, useConsentGiven } from '../../contexts/ConsentContext';
import { LayoutContextProvider } from '../../contexts/LayoutContext';
import {
    MODAL_NAMESPACE,
    NOTIFICATION_NAMESPACE,
    useHasModals,
} from '../../contexts/PopupsContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import { useVisitorId } from '../../contexts/VisitorContext';
import AccountButton from '../buttons/AccountButton';
import BrandLogoButton from '../buttons/BrandLogoButton';
import MenuButton from '../buttons/MenuButton';
import MessagesContainer from '../popups/MessagesContainer';
import NotificationsPopups from '../popups/NotificationsPopups';
import PopupsContainer from '../popups/PopupsContainer';
import PromotionsPopups from '../popups/PromotionsPopups';
import TimelinePage from './TimelinePage';

import styles from '../../styles/layouts/timeline-layout.module.css';

const MainMenu = lazy(() => import('../menus/MainMenu'));

type TimelineLayoutProps = {
    minimumContentWidth?: number;
    neighborPagesActive?: number;
    firstLoadNextDelay?: number;
    loadNextDelay?: number;
    bumperDelay?: number;
};

function TimelineLayout({
    minimumContentWidth = 600,
    neighborPagesActive = 1,
    firstLoadNextDelay = 4000,
    loadNextDelay = 1500,
    bumperDelay = 15000,
}: TimelineLayoutProps) {
    const url = useUrlGenerator();
    const user = useUser();
    const tracking = useTracking();
    const hasModals = useHasModals();
    const consentGiven = useConsentGiven();

    const currentUrl = useCurrentUrl();
    const visitorId = useVisitorId();
    const { mutate: mutateTimeline } = useMutateTimeline();
    const hasPersonalizationConsent = useConsent('personalization_storage');
    const withoutTimeline = useRoutesMatch(['preview']);
    const [withoutNext, setWithoutNext] = useState(false);

    const {
        location,
        timeline,
        index: timelineIndex,
        currentUrl: currentLocation,
        nextLoaded,
        goTo,
        navigateNext,
        navigateFromPage,
    } = useTimelineNavigation({
        firstLoadNextDelay,
        loadNextDelay,
        withoutNext,
    });
    const currentPathname = getPathname(currentLocation || location);
    const navigating = currentLocation !== null && currentLocation !== location;
    const {
        document,
        documentType,
        documentIsFetching,
        documentIsFullscreen,
        withoutNext: pageIsWithoutNext,
        isImmersive,
        isHomePage,
        isAuthPage,
        isSubscriptionPage,
        isMicromagPage,
        isTrailer,
        initialZoomedIn,
        withoutLogoInHeader,
        withSlogan,
        withoutBumperAd,
        theme,
    } = useTimelineCurrentPage(currentPathname);
    if (pageIsWithoutNext !== withoutNext) {
        setWithoutNext(pageIsWithoutNext);
    }

    const { brand: finalBrand, loaded: brandLoaded } = useBrandFromLocation(currentLocation);
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const finalDefaultBrandHandle = document !== null ? defaultBrandHandle : null;
    const { handle: brandHandle = finalDefaultBrandHandle } = finalBrand || {};
    const [globalBrand, setGlobalBrand] = useState(brandLoaded ? finalBrand : null);
    if (brandLoaded && finalBrand !== null && globalBrand !== finalBrand) {
        setGlobalBrand(finalBrand);
    }

    // const [bumperAdActive, setBumperAdActive] = useState(false);
    const [bumperAdPlaying, setBumperAdPlaying] = useState(false);
    const [menuOpened, setMenuOpened] = useState(false);
    const openMenu = () => startTransition(() => setMenuOpened(true));
    const closeMenu = () => startTransition(() => setMenuOpened(false));
    const onClickMenu = () => {
        if (menuOpened) {
            closeMenu();
        } else {
            openMenu();
        }
        tracking.trackEvent('Navigation', 'click_menu', menuOpened ? 'close' : 'open');
    };
    const onClickAccount = () => {
        tracking.trackEvent('Navigation', 'click_account');
    };
    const onClickLogo = () => {
        tracking.trackEvent('Navigation', 'click_timeline_logo', brandHandle);
    };

    const { width: windowWidth, height: windowHeight } = useWindowSize();
    const { ref: sidebarRef, width: sidebarWidth = 0 } = useElementSize();
    const { ref: containerRef, width: containerWidth = 0 } = useElementSize();
    const { ref: contentRef, width: contentWidth = windowWidth } = useElementSize();
    const { ref: brandLogoRef, height: brandLogoHeight = 70 } = useElementSize();

    const stableWindowHeight = useStableWindowHeight();
    const safeZone = Math.max(brandLogoHeight, 60);
    const safeZoneWidth = windowWidth - contentWidth * 0.02 * 2;
    const safeZoneHeight = stableWindowHeight - safeZone * 2;
    const contentScale = Math.min(safeZoneWidth / windowWidth, safeZoneHeight / stableWindowHeight);

    const sidebarOverContent = containerWidth - sidebarWidth < minimumContentWidth;

    const onClickClose = () => {
        if (menuOpened && sidebarOverContent) {
            closeMenu();
        }
    };

    const {
        visible: sidebarVisible,
        contentStyle,
        sidebarStyle,
        menuButtonStyle,
    } = useSidebarSpring({
        opened: menuOpened,
        menuButtonVisible: !hasModals && !bumperAdPlaying,
        sidebarWidth,
        sidebarOverContent,
    });

    const [menuOpenedLocation, setMenuOpenedLocation] = useState(
        menuOpened ? currentLocation : null,
    );
    if (menuOpened && menuOpenedLocation === null) {
        setMenuOpenedLocation(currentLocation);
    } else if (!menuOpened && menuOpenedLocation !== null) {
        setMenuOpenedLocation(null);
    } else if (menuOpened && menuOpenedLocation !== currentLocation) {
        setMenuOpened(false);
    }

    const onIndexChange = (newIndex) => {
        if (newIndex <= timeline.length - 1) {
            startTransition(() => {
                goTo(newIndex);
            });
        }
    };

    // The scroll only renders the layout when a threshold is crossed; animated styles follow
    // the scroll value directly.
    const scrollValue = useWindowScrollValue();
    const scrolled = useWindowScrolledPast(0);
    const scrolledPastSafeZone = useWindowScrolledPast(safeZoneHeight);

    const {
        dragging: isDragging,
        zoomInSpring,
        transitioned,
        getStyleByIndex,
        bind: dragContentBind,
        bindSwipe,
        zoomedIn,
        zoomingIn,
        zoomingOut,
        zoomIn,
        zoomOut,
        transitioning,
        direction,
    } = useTimelineSpring({
        timeline,
        timelineIndex,
        containerWidth,
        containerHeight: windowHeight,
        zoomOnDrag: !scrolled && documentType !== 'micromag',
        neighborActive: neighborPagesActive,
        initialZoomedIn,
        zoomOutScale: Number.isNaN(contentScale) ? 0.85 : contentScale,
        scrollValue,
        scrolled,
        dragWhenZoomedInDisabled: true,
        onIndexChange,
    });

    useTimelineHistory({ url: currentLocation, location, settled: !transitioning });

    const { getScroll } = useScrollRestoration({
        index: timelineIndex,
        shouldUpdate: !transitioning && !navigating && !isDragging,
        shouldReset: transitioning,
        shouldRestore: !transitioning && transitioned,
    });

    const onPageEnter = () => zoomIn();
    const onPageLeave = () => zoomOut();

    useEffect(() => {
        if (initialZoomedIn) {
            zoomIn();
        } else if (!isMicromagPage || isTrailer) {
            zoomOut();
        }
    }, [initialZoomedIn, timelineIndex, isMicromagPage, isTrailer, zoomIn, zoomOut]);

    useEffect(() => {
        if (!initialZoomedIn && zoomedIn) {
            tracking.trackEvent('Navigation', 'enter', 'zoomin');
        }
    }, [initialZoomedIn, zoomedIn]);

    useEffect(() => {
        if (isDragging) {
            tracking.trackEvent(
                'Navigation',
                'drag_timeline',
                direction >= 0 ? 'next' : 'previous',
            );
        }
    }, [isDragging, tracking]);

    const onClickNeighbor = (e, index) => {
        tracking.trackEvent(
            'Navigation',
            'click_timeline_neighbor',
            index > timelineIndex ? 'next' : 'previous',
        );
        if (timeline[index] === null) {
            navigateNext();
            return;
        }
        // Move the timeline in a transition, like a drag, instead of following the link: a
        // history push re-renders the whole layout synchronously before the click can paint.
        // The history is written once the transition settles.
        e.preventDefault();
        goTo(index);
    };

    const isFirstPage = timelineIndex === 0 && timeline.length <= 2;
    const { active: bumperAdActive, disable: disableBumperAd } = useBumperAd({
        disabled:
            isFirstPage ||
            hasModals ||
            isHomePage ||
            isAuthPage ||
            isSubscriptionPage ||
            withoutBumperAd ||
            consentGiven !== true,
    });
    const onBumperAdPlay = () => {
        setBumperAdPlaying(true);
    };
    const onBumperAdEnd = () => {
        setBumperAdPlaying(false);
        disableBumperAd();
    };

    const { ref: scrollRef } = useScrollTracking();

    const { start: visibleStartIndex, end: visibleEndIndex } = useTimelineVisibleRange(
        timelineIndex,
        {
            siblingsVisible: !zoomedIn && !zoomingIn && nextLoaded,
            transitioning,
            direction,
            maxIndex: timeline.length - 1,
        },
    );

    useEffect(() => {
        window.document.body.dataset.theme = theme || '';
        window.document.body.dataset.brand = brandHandle || 'urbania';
    }, [theme, brandHandle]);

    useEffect(() => {
        if (
            visitorId === null ||
            transitioning ||
            navigating ||
            !hasPersonalizationConsent ||
            withoutTimeline
        ) {
            return;
        }
        let timeout = null;
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => mutateTimeline(currentUrl));
        } else {
            timeout = setTimeout(() => mutateTimeline(currentUrl), 100);
        }
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
        };
    }, [
        mutateTimeline,
        currentUrl,
        visitorId,
        transitioning,
        hasPersonalizationConsent,
        withoutTimeline,
    ]);

    return (
        <LayoutContextProvider
            menuOpened={menuOpened}
            closeMenu={closeMenu}
            openMenu={openMenu}
            menuOverContent={sidebarOverContent}
            brand={globalBrand}
        >
            <div
                className={classNames([
                    styles.container,
                    {
                        [styles.withSlogan]: withSlogan,
                        [styles.isTransitioning]: isDragging || transitioning,
                        [styles.menuButtonOverContent]: !isImmersive,
                        [styles.menuOpened]: menuOpened,
                        [styles.zoomedIn]: (zoomedIn && !zoomingOut) || zoomingIn,
                        [styles.sidebarOverContent]: sidebarOverContent,
                    },
                ])}
                {...bindSwipe()}
                ref={mergeRefs(containerRef, scrollRef)}
            >
                <animated.div style={menuButtonStyle} className={styles.menuButtons}>
                    <AccountButton
                        href={user === null ? url('login') : url('account')}
                        circleColor="#222"
                        onClick={onClickAccount}
                        className={styles.accountButton}
                    />
                    <MenuButton
                        className={styles.menuButton}
                        onClick={onClickMenu}
                        circleColor="#222"
                    />
                </animated.div>

                <animated.main
                    className={styles.content}
                    style={contentStyle}
                    ref={contentRef}
                    {...dragContentBind()}
                >
                    <button
                        type="button"
                        className={styles.contentCloseButton}
                        onClick={onClickClose}
                    />

                    {!withoutLogoInHeader && globalBrand !== null ? (
                        <animated.div
                            className={styles.header}
                            style={{
                                width: contentStyle.width,
                                opacity: scrolledPastSafeZone
                                    ? 0
                                    : zoomInSpring.to((value) => 1 - value),
                            }}
                        >
                            <BrandLogoButton
                                brand={globalBrand}
                                withSponsor={zoomedIn ? false : 'auto'}
                                className={classNames([styles.brandLogo, styles[brandHandle]])}
                                logoClassName={styles.logo}
                                ref={brandLogoRef}
                                onClick={onClickLogo}
                            />
                        </animated.div>
                    ) : null}

                    {timeline.map((entry, index) => {
                        const current = index === timelineIndex;
                        const transformed = (!current || transitioning) && !documentIsFullscreen;
                        return (
                            <TimelinePage
                                key={entry !== null ? `timeline-${entry.id}` : 'timeline-next'}
                                entry={entry}
                                index={index}
                                style={getStyleByIndex(index, null)}
                                current={current}
                                next={timelineIndex + direction === index}
                                active={
                                    (index >= visibleStartIndex && index <= visibleEndIndex) ||
                                    current
                                }
                                transformed={transformed}
                                scroll={transformed ? getScroll(index) || 0 : 0}
                                transitioning={transitioning}
                                zoomedIn={zoomedIn}
                                zoomingIn={zoomingIn}
                                zoomingOut={zoomingOut}
                                paused={bumperAdPlaying || (menuOpened && sidebarOverContent)}
                                disabled={!current && documentIsFetching}
                                onEnter={current && !transitioning ? onPageEnter : null}
                                onLeave={current && !transitioning ? onPageLeave : null}
                                onNavigate={navigateFromPage}
                                onClickNeighbor={onClickNeighbor}
                            />
                        );
                    })}
                    {/* <BumperAd
                        url={currentUrl}
                        active={bumperAdActive}
                        onPlay={onBumperAdPlay}
                        onEnd={onBumperAdEnd}
                        className={styles.bumperAd}
                    /> */}
                </animated.main>

                <animated.aside className={styles.sidebar} style={sidebarStyle} ref={sidebarRef}>
                    {sidebarVisible ? (
                        <Suspense fallback={null}>
                            <MainMenu
                                opened={menuOpened}
                                lockBodyScroll={sidebarOverContent}
                                closeMenu={closeMenu}
                                className={styles.menu}
                            />
                        </Suspense>
                    ) : null}
                </animated.aside>
            </div>
            <div data-brand={brandHandle}>
                <PromotionsPopups />
                <NotificationsPopups />
                <PopupsContainer
                    namespace={MODAL_NAMESPACE}
                    activeClassName={styles.modalsOpened}
                />
                <PopupsContainer
                    namespace={NOTIFICATION_NAMESPACE}
                    className={styles.notifications}
                />
                <MessagesContainer className={styles.messages} />
            </div>
        </LayoutContextProvider>
    );
}

export default TimelineLayout;
